/**
 * Groupthink — 16 tiles split into 4 themed groups of 4. The player selects
 * four at a time and submits; correct picks lock to the top of the grid.
 * Four mistakes ends the round.
 *
 * Ported from iOS `GroupThinkPuzzle.swift` / `GroupThinkViewModel.swift`.
 */

export type GroupThinkTier = 'yellow' | 'green' | 'blue' | 'purple';

/** 0…3 — orders solved rows and drives the Purple-first bonus check. */
export const TIER_RANK: Record<GroupThinkTier, number> = {
  yellow: 0,
  green: 1,
  blue: 2,
  purple: 3,
};

export const TIER_LABEL: Record<GroupThinkTier, string> = {
  yellow: 'Easy',
  green: 'Medium',
  blue: 'Hard',
  purple: 'Tricky',
};

export interface GroupThinkGroup {
  category: string;
  words: string[];
  tier: GroupThinkTier;
}

export interface GroupThinkPuzzle {
  theme: string | null;
  groups: GroupThinkGroup[];
  maxMistakes: number;
}

/** Raw Firestore shape. */
export interface GroupThinkLevel {
  groups?: Array<{
    category?: string;
    words?: string[];
    tier?: string;
  }>;
  theme?: string | null;
  maxMistakes?: number;
  difficulty?: string;
}

function intValue(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.trunc(raw);
  if (typeof raw === 'string') {
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

const TIERS: GroupThinkTier[] = ['yellow', 'green', 'blue', 'purple'];

function isTier(s: string): s is GroupThinkTier {
  return (TIERS as string[]).includes(s);
}

/**
 * Mirrors `GroupThinkPuzzle.parse`: requires exactly 4 groups of 4 words each
 * and 16 unique words overall. Anything else is treated as malformed rather
 * than silently rendering a broken board.
 */
export function normalizePuzzle(raw: GroupThinkLevel | null): GroupThinkPuzzle | null {
  if (!raw) return null;

  const groups: GroupThinkGroup[] = [];
  for (const dict of raw.groups ?? []) {
    const category = (dict?.category ?? '').trim();
    if (!category) continue;
    const words = (dict?.words ?? []).map((w) => String(w).toUpperCase());
    if (words.length !== 4) continue;
    const tierRaw = (dict?.tier ?? 'yellow').toLowerCase();
    groups.push({
      category,
      words,
      tier: isTier(tierRaw) ? tierRaw : 'yellow',
    });
  }
  if (groups.length !== 4) return null;

  const allWords = groups.flatMap((g) => g.words);
  if (new Set(allWords).size !== 16) return null;

  return {
    theme: raw.theme ?? null,
    groups,
    maxMistakes: intValue(raw.maxMistakes) ?? 4,
  };
}

/** All 16 words flattened, deduped while preserving group order. */
export function allWords(puzzle: GroupThinkPuzzle): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const g of puzzle.groups) {
    for (const w of g.words) {
      if (!seen.has(w)) {
        seen.add(w);
        out.push(w);
      }
    }
  }
  return out;
}

export function shuffle<T>(items: T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Index of the group that contains all four picks, or -1. */
export function matchingGroupIndex(
  puzzle: GroupThinkPuzzle,
  picked: Set<string>,
): number {
  return puzzle.groups.findIndex((g) => {
    const words = new Set(g.words);
    for (const p of picked) if (!words.has(p)) return false;
    return true;
  });
}

/** True when 3 of the 4 picks belong to a single group. */
export function isOneAway(puzzle: GroupThinkPuzzle, picked: Set<string>): boolean {
  return puzzle.groups.some((g) => {
    let hits = 0;
    for (const w of g.words) if (picked.has(w)) hits++;
    return hits === 3;
  });
}
